import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Icon,
  Modal,
  Spinner,
  TextBlock,
  Title,
  useAdaptive,
} from '@skillarea/lib-frontend';
import {
  BillingTaskReturnDto,
  EBillingTaskStatus,
  EBillingTaskType,
  formatMoney,
  getBillingTaskHistoryDisplayCoins,
} from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import { accountAuthGuard } from '../-account-auth-guard';
import styles from '../balance.module.scss';

import { AccountLayout } from '~/components/Account/AccountLayout';
import { resolveHistoryPaginationState } from '~/stores/lib/history-pagination';
import { useBalance } from '~/stores/accountStore';
import { BillingTaskService } from '~/services/billingTaskService';
import { axios, getConfigs } from '~/services/options';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { BalanceAmountRolling } from '~/components/BalanceRolling/BalanceAmountRolling';
import {
  getBillingTaskStatusLabel,
  getBillingTaskTypeLabel,
} from '~/lib/billingLocale';
import {
  PAYMENT_SUCCESS_HASH,
  getNormalizedPaymentHash,
} from '~/lib/billing/payment-return';
import { usePaypalDepositReturnCapture } from '~/hooks/usePaypalDepositReturnCapture';
import { useUserStore } from '~/stores/userStore';

const billingTaskService = new BillingTaskService();
const LIMIT = 20;

type TAccountSelfData = {
  id: number;
  balance: number;
  balancePaid: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isBillingTaskStatus(value: unknown): value is EBillingTaskStatus {
  return (
    typeof value === 'number' &&
    (value === EBillingTaskStatus.created ||
      value === EBillingTaskStatus.pending ||
      value === EBillingTaskStatus.completed ||
      value === EBillingTaskStatus.failed ||
      value === EBillingTaskStatus.finished ||
      value === EBillingTaskStatus.canceled ||
      value === EBillingTaskStatus.error)
  );
}

function isBillingTaskType(value: unknown): value is EBillingTaskType {
  return (
    typeof value === 'number' &&
    (value === EBillingTaskType.coinsPurchase ||
      value === EBillingTaskType.subscriptionPurchase ||
      value === EBillingTaskType.cryptoDeposit ||
      value === EBillingTaskType.cryptoWithdraw ||
      value === EBillingTaskType.paypalDeposit ||
      value === EBillingTaskType.paypalWithdraw ||
      value === EBillingTaskType.cardDeposit)
  );
}

function isBillingTaskReturnDto(value: unknown): value is BillingTaskReturnDto {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === 'number' &&
    typeof value.price === 'number' &&
    typeof value.createdAt !== 'undefined' &&
    isBillingTaskType(value.billingTaskType) &&
    isBillingTaskStatus(value.status)
  );
}

function toBillingTaskArray(result: unknown): BillingTaskReturnDto[] {
  if (Array.isArray(result)) {
    return result.filter(isBillingTaskReturnDto);
  }

  if (!isRecord(result)) {
    return [];
  }

  if (Array.isArray(result.data)) {
    return result.data.filter(isBillingTaskReturnDto);
  }

  return [];
}

function toAccountSelfData(result: unknown): TAccountSelfData | null {
  if (isRecord(result) && isRecord(result.data)) {
    const id = result.data.id;
    const balance = result.data.balance;
    const balancePaid = result.data.balancePaid;

    if (
      typeof id === 'number' &&
      typeof balance === 'number' &&
      typeof balancePaid === 'number'
    ) {
      return { id, balance, balancePaid };
    }
  }

  if (isRecord(result)) {
    const id = result.id;
    const balance = result.balance;
    const balancePaid = result.balancePaid;

    if (
      typeof id === 'number' &&
      typeof balance === 'number' &&
      typeof balancePaid === 'number'
    ) {
      return { id, balance, balancePaid };
    }
  }

  return null;
}

async function readAccountSelfData(): Promise<TAccountSelfData | null> {
  return await new Promise((resolve, reject) => {
    const configs = getConfigs(
      'get',
      'application/json',
      '/api/account/self',
      {},
    );

    axios(
      configs,
      (response: unknown) => {
        resolve(toAccountSelfData(response));
      },
      reject,
    );
  });
}

function formatDate(value: Date | string | undefined): string {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function getStatusClassName(status: EBillingTaskStatus): string {
  if (
    status === EBillingTaskStatus.finished ||
    status === EBillingTaskStatus.completed
  ) {
    return styles.success;
  }

  if (
    status === EBillingTaskStatus.failed ||
    status === EBillingTaskStatus.canceled ||
    status === EBillingTaskStatus.error
  ) {
    return styles.fail;
  }

  return styles.pending;
}

function sortByCreatedAtDesc(
  items: BillingTaskReturnDto[],
): BillingTaskReturnDto[] {
  return [...items].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export const Route = createFileRoute('/account/balance/')({
  component: BalancePage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

function BalancePage() {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const balanceCoins = useBalance();
  const loadUser = useUserStore((state) => state.loadUser);
  const isPaypalCaptureProcessing = usePaypalDepositReturnCapture();

  const [balancePaidCoins, setBalancePaidCoins] = useState<number>(0);
  const [historyRows, setHistoryRows] = useState<BillingTaskReturnDto[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState<boolean>(true);
  const [historyLoadingMore, setHistoryLoadingMore] = useState<boolean>(false);
  const [historyCursor, setHistoryCursor] = useState<string | undefined>(
    undefined,
  );

  const isLoadingRef = useRef(false);
  const terminalPageReachedRef = useRef(false);
  const requestedCursorRef = useRef<string | undefined>(undefined);
  const loadMoreTriggerRef = useRef<globalThis.HTMLDivElement | null>(null);

  const loadInitial = useCallback(async (): Promise<void> => {
    setHistoryLoading(true);
    setHistoryError(null);
    terminalPageReachedRef.current = false;
    requestedCursorRef.current = undefined;

    try {
      const [accountSelfData, historyResult] = await Promise.all([
        readAccountSelfData(),
        billingTaskService.readHistory({ limit: LIMIT }),
      ]);

      if (accountSelfData) {
        setBalancePaidCoins(accountSelfData.balancePaid);
      }

      const responseData = sortByCreatedAtDesc(
        toBillingTaskArray(historyResult),
      );
      const paginationState = resolveHistoryPaginationState({
        responseData,
        requestCursor: undefined,
        limit: LIMIT,
      });

      terminalPageReachedRef.current = paginationState.shouldStop;
      setHistoryCursor(paginationState.nextCursor);
      setHistoryRows(responseData);
    } catch {
      setHistoryError(t('balance.loadFailed'));
    } finally {
      setHistoryLoading(false);
    }
  }, [t]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingRef.current) {
      return;
    }

    if (terminalPageReachedRef.current) {
      return;
    }

    if (!historyCursor) {
      return;
    }

    if (requestedCursorRef.current === historyCursor) {
      return;
    }

    requestedCursorRef.current = historyCursor;
    isLoadingRef.current = true;
    setHistoryLoadingMore(true);

    try {
      const response = await billingTaskService.readHistory({
        limit: LIMIT,
        cursor: historyCursor,
      });

      const responseData = sortByCreatedAtDesc(toBillingTaskArray(response));
      const paginationState = resolveHistoryPaginationState({
        responseData,
        requestCursor: historyCursor,
        limit: LIMIT,
      });

      terminalPageReachedRef.current = paginationState.shouldStop;
      setHistoryCursor(paginationState.nextCursor);
      setHistoryRows((currentRows) => {
        const rowsById = new Map<number, BillingTaskReturnDto>(
          currentRows.map((row) => [row.id, row]),
        );

        responseData.forEach((row) => {
          rowsById.set(row.id, row);
        });

        return sortByCreatedAtDesc(Array.from(rowsById.values()));
      });
    } catch {
      requestedCursorRef.current = undefined;
      setHistoryError(t('balance.loadMoreFailed'));
    } finally {
      isLoadingRef.current = false;
      setHistoryLoadingMore(false);
    }
  }, [historyCursor, t]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (
      getNormalizedPaymentHash(window.location.hash) === PAYMENT_SUCCESS_HASH
    ) {
      void loadUser();
      void loadInitial();
    }
  }, [loadInitial, loadUser]);

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger) {
      return;
    }

    const observer = new globalThis.IntersectionObserver(
      (entries) => {
        const hasIntersectingEntry = entries.some(
          (entry) => entry.isIntersecting,
        );
        if (hasIntersectingEntry) {
          void loadMore();
        }
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0,
      },
    );

    observer.observe(trigger);
    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  const bonusBalanceCoins = useMemo(() => {
    return balanceCoins - balancePaidCoins;
  }, [balanceCoins, balancePaidCoins]);

  const paymentTableRows = historyRows.filter((task) => {
    return (
      isBillingTaskType(task.billingTaskType) &&
      isBillingTaskStatus(task.status)
    );
  });

  const { isMobile, isTablet } = useAdaptive();

  const titleSize = isMobile ? 'xs' : isTablet ? 's' : 'm';

  return (
    <AccountLayout>
      <div className={styles.container}>
        <div className={styles.balanceContainer}>
          <LayoutCard
            className={styles.balanceCard}
            title={
              <div className={styles.balanceTitle}>
                <Title color="contrast" size={titleSize} variant="benzin">
                  {t('balance.title')}
                </Title>
                <div className={styles.balanceActions}>
                  <Button
                    color="primary"
                    data-testid="balance-add-button"
                    iconRight={<Icon color="contrast" img="plus" />}
                    onClick={() => {
                      void navigate({
                        to: '/account/balance/deposit',
                      });
                    }}
                    size="s"
                    type="button"
                    variant="filled"
                  >
                    {t('balance.add')}
                  </Button>

                  <Button
                    color="contrast"
                    data-testid="balance-withdrawal-button"
                    iconRight={<Icon color="text" img="credit-card" />}
                    onClick={() => {
                      void navigate({
                        to: '/account/balance/withdrawal',
                      });
                    }}
                    size="s"
                    type="button"
                    variant="filled"
                  >
                    {t('balance.withdrawal')}
                  </Button>
                </div>
              </div>
            }
          >
            {historyLoading ? (
              <BalanceAmountRolling
                amountClassName={styles.balanceUsd}
                ariaLabel={t('balance.loadingBalance')}
                color="accent"
              />
            ) : (
              <Title
                className={styles.balanceUsd}
                color="accent"
                data-testid="balance-total-usd"
                size="xl"
              >
                {formatMoney(balancePaidCoins, { maxDigits: 0 })}
              </Title>
            )}
          </LayoutCard>

          <LayoutCard
            backgroundColor="accent"
            className={styles.bonusCard}
            title={
              <div className={styles.balanceTitle}>
                <Title color="text" size={titleSize} variant="benzin">
                  {t('balance.bonus')}
                </Title>
              </div>
            }
            titleSize={titleSize}
          >
            {historyLoading ? (
              <BalanceAmountRolling
                amountClassName={styles.balanceBonus}
                ariaLabel={t('balance.loadingBonus')}
                color="text"
              />
            ) : (
              <Title
                className={styles.balanceBonus}
                color="text"
                data-testid="balance-bonus-usd"
                size="xl"
              >
                {formatMoney(bonusBalanceCoins, { maxDigits: 0 })}
              </Title>
            )}
          </LayoutCard>
        </div>

        <LayoutCard title={t('balance.paymentHistory')} titleSize={titleSize}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('balance.headers.date')}</th>
                  <th>{t('balance.headers.amount')}</th>
                  <th>{t('balance.headers.type')}</th>
                  <th>{t('balance.headers.status')}</th>
                </tr>
              </thead>
              <tbody>
                {paymentTableRows.map((task) => (
                  <tr key={task.id}>
                    <td>{formatDate(task.createdAt)}</td>
                    <td>
                      {formatMoney(getBillingTaskHistoryDisplayCoins(task), {
                        nullValue: '-',
                      })}
                    </td>
                    <td>{getBillingTaskTypeLabel(t, task.billingTaskType)}</td>
                    <td>
                      <span
                        className={cx(
                          styles.result,
                          getStatusClassName(task.status),
                        )}
                      >
                        {getBillingTaskStatusLabel(t, task.status)}
                      </span>
                    </td>
                  </tr>
                ))}
                {!historyLoading && paymentTableRows.length === 0 ? (
                  <tr>
                    <td className={styles.noData} colSpan={4}>
                      {t('balance.empty')}
                    </td>
                  </tr>
                ) : null}
                {historyLoading ? (
                  <tr>
                    <td className={styles.noData} colSpan={4}>
                      {t('balance.loading')}
                    </td>
                  </tr>
                ) : null}
                {historyError ? (
                  <tr>
                    <td className={styles.noData} colSpan={4}>
                      {historyError}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div
            aria-hidden
            className={styles.loadMore}
            ref={loadMoreTriggerRef}
          />
          {historyLoadingMore ? (
            <TextBlock
              className={styles.loadMoreText}
              color="secondary"
              size="s"
            >
              {t('balance.loadingMore')}
            </TextBlock>
          ) : null}
        </LayoutCard>
      </div>

      <Modal
        hideCloseButton
        isAutoClosing={false}
        isOpen={isPaypalCaptureProcessing}
        preventOutsideClose
        title={t('deposit.processingTitle')}
      >
        <div data-testid="paypal-processing-modal">
          <Spinner />
          <TextBlock color="secondary">{t('deposit.processingText')}</TextBlock>
        </div>
      </Modal>
    </AccountLayout>
  );
}
